/** A structural-audit execution row returned by the governance service. */
export interface ExecutionSummary {
  executionId: string
  hostId: string | null
  startedAt: string
  endedAt: string
  totalEvents: number
  agentTurns: number
  toolCalls: number
  hitlEvents: number
  tokensInput: number
  tokensOutput: number
  totalDurationMs: number
  agentIds: string[]
  allSuccess: boolean
  terminalStatus?: string
  terminalSource?: string
  learningEligibleCount: number
  promotedLearningCount: number
  policyDeniedCount: number
  /**
   * Whether the run's answer met its success criteria, apart from how the run
   * ended (agion/Agion#2395). Sent only when asked (includeResultVerdict) and
   * only for a run with criteria verdicts.
   */
  resultVerdict?: ResultVerdict
}

/** A run's result verdict: not_met when any criterion was not met, met when all were, unknown otherwise. */
export type ResultVerdict = 'met' | 'not_met' | 'unknown'

/** Counts over the complete filtered structural-audit execution population. */
export interface ExecutionRunMetrics {
  total: number
  succeeded: number
  failed: number
  cancelled: number
  blockedAuthorization: number
  blockedContract: number
  degradedDependency: number
  stoppedByPolicy: number
  inProgress: number
  /** Unrecognised terminal statuses reported by the backend. */
  otherTerminal: number
  /**
   * The server's result verdict counts over the same runs as `total`
   * (agion/Agion#2395): sent only when asked (includeResultVerdict). Not
   * judged counts runs with no verdict; it is never "not met".
   */
  resultVerdicts?: ResultVerdictCounts
  /**
   * The newest run of the `failed` bucket and the runner's codes on the
   * mission_end row that decided it (agion/Agion#2515): sent only when asked
   * (includeLastFailure) and `failed` is not zero. The codes are raw; word
   * them through runEndingReading only.
   */
  lastFailure?: ExecutionLastFailure
}

/** The period's newest failed run, as the server names it. */
export interface ExecutionLastFailure {
  executionId: string
  endedAt: string
  outcome?: string
  errorCategory?: string
}

/** How many of the counted runs the server judged met, not met, unknown, or did not judge. */
export interface ResultVerdictCounts {
  met: number
  notMet: number
  unknown: number
  notJudged: number
}

export interface ExecutionSummaryResponse {
  items: ExecutionSummary[]
  total: number
  limit: number
  offset: number
  missionId: string
  runMetrics?: ExecutionRunMetrics
}

/**
 * Terminal status is authoritative when the backend supplies it. `allSuccess`
 * remains only as a compatibility fallback for older responses.
 */
export function executionTerminalStatus(summary: Pick<ExecutionSummary, 'terminalStatus' | 'allSuccess'>): string {
  return summary.terminalStatus?.trim() || (summary.allSuccess ? 'succeeded' : 'failed_execution')
}

/** A non-negative integer from a camelized response or a raw wire fixture, else undefined. */
function countField(record: Record<string, unknown>, camel: string, snake: string): number | undefined {
  const candidate = record[camel] ?? record[snake]
  return typeof candidate === 'number' && Number.isFinite(candidate) && Number.isInteger(candidate) && candidate >= 0
    ? candidate
    : undefined
}

/**
 * The server's verdict counts, accepted only when all four are present and
 * count exactly the runs in `total`. Anything else is dropped, never
 * recomputed in the browser (ADR-20261002).
 */
function resultVerdictCountsOf(value: unknown, total: number): ResultVerdictCounts | undefined {
  if (!value || typeof value !== 'object') return undefined
  const counts = value as Record<string, unknown>
  const met = countField(counts, 'met', 'met')
  const notMet = countField(counts, 'notMet', 'not_met')
  const unknown = countField(counts, 'unknown', 'unknown')
  const notJudged = countField(counts, 'notJudged', 'not_judged')
  if (met === undefined || notMet === undefined || unknown === undefined || notJudged === undefined) return undefined
  if (met + notMet + unknown + notJudged !== total) return undefined
  return { met, notMet, unknown, notJudged }
}

/**
 * The server's last failed run, accepted only with its run id and end time;
 * the optional codes are kept only as non-empty strings.
 */
function lastFailureOf(value: unknown): ExecutionLastFailure | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const text = (camel: string, snake: string): string | undefined => {
    const candidate = record[camel] ?? record[snake]
    return typeof candidate === 'string' && candidate.trim() !== '' ? candidate : undefined
  }
  const executionId = text('executionId', 'execution_id')
  const endedAt = text('endedAt', 'ended_at')
  if (!executionId || !endedAt) return undefined
  const outcome = text('outcome', 'outcome')
  const errorCategory = text('errorCategory', 'error_category')
  return { executionId, endedAt, ...(outcome ? { outcome } : {}), ...(errorCategory ? { errorCategory } : {}) }
}

/**
 * Accept camelized responses and raw wire fixtures only when every terminal
 * bucket is present and forms a complete population partition. Partial or
 * malformed projections remain unavailable rather than fabricating zeros.
 */
export function executionRunMetricsOf(value: unknown): ExecutionRunMetrics | undefined {
  if (!value || typeof value !== 'object') return undefined
  const metrics = value as Record<string, unknown>
  const numberField = (camel: string, snake: string): number | undefined => countField(metrics, camel, snake)

  const total = numberField('total', 'total')
  const succeeded = numberField('succeeded', 'succeeded')
  const failed = numberField('failed', 'failed')
  const cancelled = numberField('cancelled', 'cancelled')
  const blockedAuthorization = numberField('blockedAuthorization', 'blocked_authorization')
  const blockedContract = numberField('blockedContract', 'blocked_contract')
  const degradedDependency = numberField('degradedDependency', 'degraded_dependency')
  const stoppedByPolicy = numberField('stoppedByPolicy', 'stopped_by_policy')
  const inProgress = numberField('inProgress', 'in_progress')
  const otherTerminal = numberField('otherTerminal', 'other_terminal')

  if (
    total === undefined || succeeded === undefined || failed === undefined || cancelled === undefined ||
    blockedAuthorization === undefined || blockedContract === undefined || degradedDependency === undefined ||
    stoppedByPolicy === undefined || inProgress === undefined || otherTerminal === undefined
  ) {
    return undefined
  }
  if (total !== succeeded + failed + cancelled + blockedAuthorization + blockedContract + degradedDependency + stoppedByPolicy + inProgress + otherTerminal) {
    return undefined
  }

  const resultVerdicts = resultVerdictCountsOf(metrics['resultVerdicts'] ?? metrics['result_verdicts'], total)
  const lastFailure = failed > 0 ? lastFailureOf(metrics['lastFailure'] ?? metrics['last_failure']) : undefined
  return {
    total,
    succeeded,
    failed,
    cancelled,
    blockedAuthorization,
    blockedContract,
    degradedDependency,
    stoppedByPolicy,
    inProgress,
    otherTerminal,
    ...(resultVerdicts ? { resultVerdicts } : {}),
    ...(lastFailure ? { lastFailure } : {}),
  }
}
